import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, CheckCircle, XCircle, Clock, CreditCard, Phone, User, LogOut } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import "../styles/Admin.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    verifyAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
    }
  }, [isAuthenticated]);

  const verifyAuth = async () => {
    const token = localStorage.getItem("admin_token");
    
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/admin/verify`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    } catch (error) {
      console.error("Auth verification error:", error);
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const fetchBookings = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/all`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setBookings(data.bookings);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/status?status=${newStatus}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        fetchBookings(); // Refresh bookings
      }
    } catch (error) {
      console.error("Error updating booking:", error);
    }
  };

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  const filteredBookings = filter === "all" 
    ? bookings 
    : bookings.filter(b => b.status === filter);

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed": return <CheckCircle size={20} color="#22c55e" />;
      case "pending": return <Clock size={20} color="#f59e0b" />;
      case "cancelled": return <XCircle size={20} color="#ef4444" />;
      case "completed": return <CheckCircle size={20} color="#3b82f6" />;
      default: return <Clock size={20} />;
    }
  };

  const getPaymentStatusBadge = (status) => {
    const classes = `payment-badge ${status}`;
    return <span className={classes}>{status}</span>;
  };

  return (
    <div className="admin-page">
      <Navigation />
      
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h1 className="heading-1">Booking Dashboard</h1>
            <p className="body-large">Manage your appointments and view payment status</p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={20} />
            Logout
          </button>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{background: "rgba(147, 51, 234, 0.1)"}}>
              <Calendar size={32} color="var(--purple-primary)" />
            </div>
            <div>
              <p className="stat-number">{bookings.length}</p>
              <p className="stat-label">Total Bookings</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{background: "rgba(34, 197, 94, 0.1)"}}>
              <CheckCircle size={32} color="#22c55e" />
            </div>
            <div>
              <p className="stat-number">{bookings.filter(b => b.status === "confirmed").length}</p>
              <p className="stat-label">Confirmed</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{background: "rgba(245, 158, 11, 0.1)"}}>
              <Clock size={32} color="#f59e0b" />
            </div>
            <div>
              <p className="stat-number">{bookings.filter(b => b.status === "pending").length}</p>
              <p className="stat-label">Pending</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{background: "rgba(247, 179, 43, 0.1)"}}>
              <CreditCard size={32} color="var(--gold-primary)" />
            </div>
            <div>
              <p className="stat-number">£{bookings.reduce((sum, b) => sum + (b.deposit_amount || 0), 0).toFixed(2)}</p>
              <p className="stat-label">Total Deposits</p>
            </div>
          </div>
        </div>

        <div className="filters">
          <button 
            onClick={() => setFilter("all")} 
            className={`filter-btn ${filter === "all" ? "active" : ""}`}
          >
            All ({bookings.length})
          </button>
          <button 
            onClick={() => setFilter("pending")} 
            className={`filter-btn ${filter === "pending" ? "active" : ""}`}
          >
            Pending ({bookings.filter(b => b.status === "pending").length})
          </button>
          <button 
            onClick={() => setFilter("confirmed")} 
            className={`filter-btn ${filter === "confirmed" ? "active" : ""}`}
          >
            Confirmed ({bookings.filter(b => b.status === "confirmed").length})
          </button>
          <button 
            onClick={() => setFilter("completed")} 
            className={`filter-btn ${filter === "completed" ? "active" : ""}`}
          >
            Completed ({bookings.filter(b => b.status === "completed").length})
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading bookings...</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="empty-state">
            <Calendar size={64} color="var(--text-muted)" />
            <h3 className="heading-3">No bookings yet</h3>
            <p className="body-regular">Bookings will appear here once customers start booking</p>
          </div>
        ) : (
          <div className="bookings-table">
            {filteredBookings.map((booking) => (
              <div key={booking.booking_id} className="booking-row">
                <div className="booking-main">
                  <div className="booking-service">
                    <h3 className="heading-3">{booking.service_title}</h3>
                    <p className="body-small">{booking.service_price && `Full Price: £${booking.service_price}`}</p>
                  </div>
                  <div className="booking-datetime">
                    <div className="booking-info-item">
                      <Calendar size={16} />
                      <span>{booking.appointment_date}</span>
                    </div>
                    <div className="booking-info-item">
                      <Clock size={16} />
                      <span>{booking.appointment_time}</span>
                    </div>
                  </div>
                  <div className="booking-customer">
                    <div className="booking-info-item">
                      <User size={16} />
                      <span>{booking.customer_name}</span>
                    </div>
                    <div className="booking-info-item">
                      <Phone size={16} />
                      <span>{booking.customer_phone}</span>
                    </div>
                  </div>
                  <div className="booking-payment">
                    <div className="booking-info-item">
                      <CreditCard size={16} />
                      <span>£{booking.deposit_amount} deposit</span>
                    </div>
                    {getPaymentStatusBadge(booking.payment_status || "pending")}
                  </div>
                </div>
                <div className="booking-actions">
                  <div className="status-indicator">
                    {getStatusIcon(booking.status)}
                    <span className="status-text">{booking.status}</span>
                  </div>
                  <div className="action-buttons">
                    {booking.status === "pending" && (
                      <button 
                        onClick={() => updateBookingStatus(booking.booking_id, "confirmed")}
                        className="btn-confirm"
                      >
                        Confirm
                      </button>
                    )}
                    {booking.status === "confirmed" && (
                      <button 
                        onClick={() => updateBookingStatus(booking.booking_id, "completed")}
                        className="btn-complete"
                      >
                        Mark Complete
                      </button>
                    )}
                    {(booking.status === "pending" || booking.status === "confirmed") && (
                      <button 
                        onClick={() => updateBookingStatus(booking.booking_id, "cancelled")}
                        className="btn-cancel"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;